import { useCallback, useMemo } from 'react';
import { keepPreviousData, Updater, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import { AppID, Filter } from '@playq/octopus-common';
import { PackageID } from '@playq/services-bookkeeper';
import {
  InventoryPackagePromotions,
  ItemDetails,
  PackagesSort,
  PackagesSortField,
  QueryInventoryPackagesResponse,
} from '@playq/octopus2-economy';
import { PermModule } from '@playq/octopus2-auth';

import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { appToolkit } from '/store';
import { QueryHelpers } from '/helpers';
import { useModulePermissions } from '/hooks';

import { IEntityPromotion } from '../promotions';

import { appsPackagesQueryKeys, appsPackagesService } from './constants';

const emptyArray: [] = [];
const defaultItemDetails: Record<string, ItemDetails | undefined> = {};

const initQuery = QueryHelpers.getInitValue<PackagesSortField, PackagesSort>();
export const useAppsPackagesQuery = (
  iterator: OffsetLimit = initQuery.iterator,
  sortBy: PackagesSort[] = initQuery.sortBy,
  filterBy: {
    [key: string]: Filter;
  } = initQuery.filterBy,
  options?: IUseQueryOptionsExtended<QueryInventoryPackagesResponse | undefined, GenericFailure | Error>
) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const queryClient = useQueryClient();

  const { canRead } = useModulePermissions(PermModule.EconomyPackages);

  const keys = useMemo(
    () => appsPackagesQueryKeys.concat(appID, iterator, sortBy, filterBy),
    [appID, iterator, sortBy, filterBy]
  );

  const isValid = appID !== undefined && canRead;
  const enabled = (options?.enabled ?? true) && isValid;
  const enablePrefetch = (options?.enablePrefetch ?? true) && isValid;

  const { mutate, refetch, ...res } = useEitherQuery(
    appsPackagesQueryKeys.concat(appID, iterator, sortBy, filterBy),
    () => appsPackagesService.queryPackages(appID as AppID, iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      ...options,
      enabled,
    }
  );

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  useQueryPrefetch({
    keys: appsPackagesQueryKeys.concat(appID, iterator, sortBy, filterBy),
    enabled: enablePrefetch,
    total,
    args: [appID as AppID, iterator, sortBy, filterBy],
    serviceMethod: appsPackagesService.queryPackages.bind(appsPackagesService),
  });

  const mutatePromotion = useCallback(
    (promotions: Updater<IEntityPromotion<PackageID>[], IEntityPromotion<PackageID>[]>) => {
      mutate((prevResponse: QueryInventoryPackagesResponse | undefined) => {
        if (!prevResponse) {
          return;
        }
        const response = new QueryInventoryPackagesResponse(prevResponse.serialize());
        const nextPromtions: IEntityPromotion<PackageID>[] =
          typeof promotions === 'function' ? promotions(response.promotions) : promotions;

        response.promotions = nextPromtions.map(
          (p: IEntityPromotion<PackageID>) =>
            new InventoryPackagePromotions({
              id: p.id.serialize(),
              promotions: p.promotions.serialize(),
            })
        );

        return response;
      });
    },
    // mutate deps lead to event loop queryScriptRuntime and queryPromotionBatch, if user selected package
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const removeQueries = useCallback(() => {
    queryClient.removeQueries({ queryKey: appsPackagesQueryKeys });
  }, [queryClient]);

  return {
    ...res,
    packages: res.data?.packages || emptyArray,
    promotions: res.data?.promotions ?? ([] as IEntityPromotion<PackageID>[]),
    itemsDetails: res.data?.itemDetails ?? defaultItemDetails,
    total: res.data?.total ?? 0,
    tags: keys,
    mutatePromotion,
    removeQueries,
    refetch,
  };
};
